import "./templates-mobile.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	refreshCcw_03,
	searchSm,
	xClose,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo, useState } from "react";

import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";

import { DeleteTemplateModal } from "../delete-template-modal/delete-template-modal.tsx";
import { TemplateCard } from "../template-card/template-card.tsx";

type TemplatesMobileProps = {
	templates: TemplateGridItem[] | undefined;
	customerId: number;
	customerName: string;
	isLoading: boolean;
	isBusy: boolean;
	onEdit: (templateId: number) => void;
	onRefresh: () => void;
	toggleDefault: (templateId: number, shouldBeDefault: boolean) => void;
	deleteTemplate: (templateId: number) => void | Promise<void>;
	isDefaultTemplate: (templateId: number) => boolean;
	isInternal?: boolean;
	onClearCustomer?: () => void;
};

export const TemplatesMobile = ({
	templates,
	customerId,
	customerName,
	isLoading,
	isBusy,
	onEdit,
	onRefresh,
	toggleDefault,
	deleteTemplate,
	isDefaultTemplate,
	isInternal = false,
	onClearCustomer,
}: TemplatesMobileProps) => {
	const [searchText, setSearchText] = useState("");
	const [templateToDelete, setTemplateToDelete] = useState<
		TemplateGridItem | undefined
	>(undefined);

	const filteredTemplates = useMemo(() => {
		const list = templates ?? [];
		const normalizedSearch = searchText.trim().toLowerCase();
		if (!normalizedSearch) return list;

		return list.filter((template) =>
			[
				template.templateName,
				template.shipperName,
				template.pickupStreet,
				template.pickupLocation,
				template.deliveryName,
				template.deliveryStreet,
				template.deliveryLocation,
				template.commodity,
				template.nmfc,
				template.freightClass,
				template.quantity,
				template.account,
			].some((value) => value.toLowerCase().includes(normalizedSearch)),
		);
	}, [templates, searchText]);

	const handleConfirmDelete = useCallback(() => {
		if (templateToDelete) {
			Promise.resolve(deleteTemplate(templateToDelete.templateId)).catch(
				() => undefined,
			);
		}
		setTemplateToDelete(undefined);
	}, [deleteTemplate, templateToDelete]);

	let content;
	if (isLoading) {
		content = (
			<div className={"templates-mobile-state"} role={"status"}>
				Loading templates...
			</div>
		);
	} else if (filteredTemplates.length > 0) {
		content = (
			<div aria-label={"Templates"} className={"templates-mobile-list"}>
				{filteredTemplates.map((template) => (
					<TemplateCard
						key={template.templateId}
						isBusy={isBusy}
						isDefault={isDefaultTemplate(template.templateId)}
						onDelete={setTemplateToDelete}
						onEdit={onEdit}
						onToggleDefault={toggleDefault}
						template={template}
					/>
				))}
			</div>
		);
	} else {
		content = (
			<div className={"templates-mobile-state"} role={"status"}>
				No matching templates found.
			</div>
		);
	}

	return (
		<div className={"templates-mobile"} data-testid={"templates-mobile"}>
			<div className={"templates-mobile-card templates-mobile-header-card"}>
				<div className={"templates-mobile-customer-row"}>
					<h2
						className={"templates-mobile-customer-title"}
						data-testid={"templates-mobile-customer-title"}
					>
						{customerName} ({customerId})
					</h2>
					{isInternal && onClearCustomer ? (
						<Button
							aria-label={"Clear selected customer"}
							data-testid={"templates-mobile-clear-customer"}
							onClick={onClearCustomer}
							svgIcon={xClose}
							variant={"tertiary"}
						/>
					) : null}
				</div>
				<section
					aria-label={"Template search"}
					className={"templates-mobile-search-panel"}
				>
					<div className={"templates-mobile-search-row"}>
						<Button
							aria-label={"Refresh templates"}
							data-testid={"templates-mobile-refresh"}
							onClick={onRefresh}
							svgIcon={refreshCcw_03}
							variant={"tertiary"}
						/>
						<div className={"templates-mobile-search-input-wrapper"}>
							<SvgIcon
								className={"templates-mobile-search-icon"}
								icon={searchSm}
							/>
							<input
								aria-label={"Search templates"}
								className={"templates-mobile-search"}
								data-testid={"templates-mobile-search"}
								id={"template-search"}
								onChange={(event) => setSearchText(event.target.value)}
								placeholder={"Search"}
								value={searchText}
							/>
						</div>
					</div>
				</section>
			</div>

			{content}

			{templateToDelete ? (
				<DeleteTemplateModal
					onClose={() => setTemplateToDelete(undefined)}
					onConfirm={handleConfirmDelete}
					templateName={templateToDelete.templateName}
				/>
			) : null}
		</div>
	);
};
